/**
 * /admin/users — admin-only user management list.
 *
 * - Paginated, filterable table of all users.
 * - "Invite User" button opens a modal with email + role.
 * - Per-row actions: open detail, resend invite, disable/enable.
 *
 * Heavier actions (role change, revoke sessions) live in UserDetail.tsx.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  adminUsersApi,
  type AdminUserSummary,
  type UserRole,
  type UserStatus,
} from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  Button,
  Input,
  Card,
  CardContent,
  Spinner,
  Modal,
} from '@/components/common';
import {
  Users,
  Plus,
  Mail,
  Search,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Shield,
  Briefcase,
  Building2,
  Info,
} from 'lucide-react';

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function StatusBadge({ status }: { status: UserStatus }) {
  const styles: Record<UserStatus, string> = {
    active: 'bg-emerald-100 text-emerald-700',
    pending: 'bg-amber-100 text-amber-700',
    disabled: 'bg-gray-200 text-gray-600',
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded font-medium ${styles[status]}`}>
      {status}
    </span>
  );
}

function RoleBadge({ role }: { role: UserRole }) {
  const cls =
    role === 'admin'
      ? 'bg-primary/10 text-primary'
      : role === 'partner'
        ? 'bg-violet-50 text-violet-700'
        : 'bg-blue-50 text-blue-700';
  return (
    <span className={`text-xs px-2 py-0.5 rounded font-medium ${cls}`}>
      {role}
    </span>
  );
}

function InviteModal({
  open,
  onClose,
  onInvited,
  actorRole,
}: {
  open: boolean;
  onClose: () => void;
  onInvited: (user: AdminUserSummary) => void;
  /** Drives which Role options the form offers. Admins see admin /
   *  partner / user; partners are hard-locked to 'user'. */
  actorRole: 'admin' | 'partner' | 'user';
}) {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [company, setCompany] = useState('');
  const [role, setRole] = useState<UserRole>('user');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setEmail('');
    setUsername('');
    setCompany('');
    setRole('user');
    setError(null);
    setSubmitting(false);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      // Partners cannot invite anything but 'user' — enforce client-side
      // even though the backend also blocks it (defense in depth).
      const finalRole: UserRole = actorRole === 'partner' ? 'user' : role;
      const user = await adminUsersApi.invite(
        email.trim().toLowerCase(),
        finalRole,
        username.trim(),
        company.trim() ? company.trim() : null,
      );
      onInvited(user);
      reset();
      onClose();
    } catch (err: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const detail = (err as any)?.response?.data?.detail || 'invite failed';
      setError(typeof detail === 'string' ? detail : 'invite failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={open} onClose={() => { reset(); onClose(); }} title="Invite User">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="flex items-start gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-600">
            <AlertTriangle size={20} />
            <span className="text-sm">{error}</span>
          </div>
        )}

        <Input
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="newuser@example.com"
          leftIcon={<Mail size={18} />}
          fullWidth
          required
          autoComplete="off"
          disabled={submitting}
        />

        <Input
          label="Username"
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="Jane Doe"
          leftIcon={<Briefcase size={18} />}
          fullWidth
          required
          maxLength={100}
          autoComplete="off"
          disabled={submitting}
        />

        <Input
          label="Company (optional)"
          type="text"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          placeholder="Acme Corp"
          leftIcon={<Building2 size={18} />}
          fullWidth
          maxLength={200}
          autoComplete="off"
          disabled={submitting}
        />

        {actorRole === 'partner' ? (
          <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded p-2">
            Partners can only invite users with the <strong>User</strong> role.
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
            <div className="space-y-2">
              <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="role"
                  value="user"
                  checked={role === 'user'}
                  onChange={() => setRole('user')}
                  disabled={submitting}
                  className="mt-1"
                />
                <div>
                  <div className="font-medium text-gray-900">User</div>
                  <div className="text-xs text-gray-500">Read-only access to dashboards. Cannot manage nodes or invite others.</div>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="role"
                  value="partner"
                  checked={role === 'partner'}
                  onChange={() => setRole('partner')}
                  disabled={submitting}
                  className="mt-1"
                />
                <div>
                  <div className="font-medium text-gray-900">Partner</div>
                  <div className="text-xs text-gray-500">Can invite + manage their own users; sees Data Room and operational dashboards.</div>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="role"
                  value="admin"
                  checked={role === 'admin'}
                  onChange={() => setRole('admin')}
                  disabled={submitting}
                  className="mt-1"
                />
                <div>
                  <div className="font-medium text-gray-900 flex items-center gap-2">
                    Admin <Shield size={14} className="text-primary" />
                  </div>
                  <div className="text-xs text-gray-500">Full access including node lifecycle and user management.</div>
                </div>
              </label>
            </div>
          </div>
        )}

        <p className="text-xs text-gray-500">
          An invitation email will be sent to this address. The link expires
          in 72 hours.
        </p>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="ghost" onClick={() => { reset(); onClose(); }} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            isLoading={submitting}
            disabled={!email || !username.trim() || submitting}
          >
            Send Invitation
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export default function UsersPage() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const actorRole: 'admin' | 'partner' | 'user' =
    currentUser?.role === 'admin'
      ? 'admin'
      : currentUser?.role === 'partner'
        ? 'partner'
        : 'user';
  const [users, setUsers] = useState<AdminUserSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const perPage = 25;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<UserStatus | ''>('');
  const [roleFilter, setRoleFilter] = useState<UserRole | ''>('');

  const [inviteOpen, setInviteOpen] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminUsersApi.list({
        status: statusFilter || undefined,
        role: roleFilter || undefined,
        search: search || undefined,
        page,
        per_page: perPage,
      });
      setUsers(data.items);
      setTotal(data.total);
    } catch (err: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const detail = (err as any)?.response?.data?.detail || 'could not load users';
      setError(typeof detail === 'string' ? detail : 'could not load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter, roleFilter]);

  const handleSearchSubmit = (e: FormEvent) => {
    e.preventDefault();
    setPage(1);
    load();
  };

  const handleResend = async (u: AdminUserSummary) => {
    try {
      await adminUsersApi.resendInvite(u.id);
      setFlash(`Invitation resent to ${u.email}`);
      load();
    } catch (err: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const detail = (err as any)?.response?.data?.detail || 'resend failed';
      alert(`Resend failed: ${typeof detail === 'string' ? detail : 'unknown'}`);
    }
  };

  const handleDisable = async (u: AdminUserSummary) => {
    if (!window.confirm(`Disable ${u.email}? They lose access immediately and any active sessions are revoked.`)) return;
    try {
      await adminUsersApi.disable(u.id);
      setFlash(`Disabled ${u.email}`);
      load();
    } catch (err: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const detail = (err as any)?.response?.data?.detail || 'disable failed';
      alert(`Disable failed: ${typeof detail === 'string' ? detail : 'unknown'}`);
    }
  };

  const handleEnable = async (u: AdminUserSummary) => {
    try {
      await adminUsersApi.enable(u.id);
      setFlash(`Re-enabled ${u.email}`);
      load();
    } catch (err: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const detail = (err as any)?.response?.data?.detail || 'enable failed';
      alert(`Enable failed: ${typeof detail === 'string' ? detail : 'unknown'}`);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / perPage));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
            <Users size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Users</h2>
            <p className="text-sm text-gray-500">Manage who has access to DDARP Manager.</p>
          </div>
        </div>
        <Button variant="primary" leftIcon={<Plus size={16} />} onClick={() => setInviteOpen(true)}>
          Invite User
        </Button>
      </div>

      {flash && (
        <div className="flex items-start gap-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-700">
          <CheckCircle2 size={20} />
          <span className="text-sm">{flash}</span>
          <button className="ml-auto text-xs underline" onClick={() => setFlash(null)}>
            dismiss
          </button>
        </div>
      )}

      {actorRole === 'partner' && (
        <div className="flex items-start gap-3 p-3 rounded-lg bg-violet-50 border border-violet-200 text-violet-800">
          <Info size={18} className="mt-0.5 flex-shrink-0" />
          <span className="text-sm">
            Showing only users you invited. To see the full list, ask an admin.
          </span>
        </div>
      )}

      <Card>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <form onSubmit={handleSearchSubmit} className="flex items-center gap-2 flex-1 min-w-[200px]">
              <Input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by email"
                leftIcon={<Search size={16} />}
                fullWidth
              />
              <Button type="submit" variant="ghost" size="sm">Search</Button>
            </form>

            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value as UserStatus | ''); setPage(1); }}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white"
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="disabled">Disabled</option>
            </select>

            <select
              value={roleFilter}
              onChange={(e) => { setRoleFilter(e.target.value as UserRole | ''); setPage(1); }}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white"
            >
              <option value="">All roles</option>
              <option value="admin">Admin</option>
              <option value="partner">Partner</option>
              <option value="user">User</option>
            </select>

            <Button variant="ghost" size="sm" leftIcon={<RefreshCw size={14} />} onClick={load}>
              Refresh
            </Button>
          </div>

          {error && (
            <div className="flex items-start gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-600 mb-4">
              <AlertTriangle size={20} />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {loading ? (
            <div className="py-8 text-center"><Spinner /></div>
          ) : users.length === 0 ? (
            <p className="py-8 text-center text-gray-500">No users match the current filters.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-gray-500 border-b border-gray-200">
                  <tr>
                    <th className="py-2 pr-3">Email</th>
                    <th className="py-2 pr-3">Username</th>
                    <th className="py-2 pr-3">Company</th>
                    <th className="py-2 pr-3">Role</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Last sign-in</th>
                    <th className="py-2 pr-3">Created</th>
                    <th className="py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-2 pr-3">
                        <button
                          className="text-primary hover:underline font-medium"
                          onClick={() => navigate(`/admin/users/${u.id}`)}
                        >
                          {u.email}
                        </button>
                      </td>
                      <td className="py-2 pr-3 text-gray-700">{u.username || '—'}</td>
                      <td className="py-2 pr-3 text-gray-600">{u.company || '—'}</td>
                      <td className="py-2 pr-3"><RoleBadge role={u.role} /></td>
                      <td className="py-2 pr-3"><StatusBadge status={u.status} /></td>
                      <td className="py-2 pr-3 text-gray-600">{formatDate(u.last_login_at)}</td>
                      <td className="py-2 pr-3 text-gray-600">{formatDate(u.created_at)}</td>
                      <td className="py-2 text-right space-x-1">
                        {u.status === 'pending' && (
                          <Button variant="ghost" size="sm" onClick={() => handleResend(u)}>
                            Resend
                          </Button>
                        )}
                        {u.status === 'disabled' ? (
                          <Button variant="ghost" size="sm" onClick={() => handleEnable(u)}>
                            Enable
                          </Button>
                        ) : (
                          <Button variant="ghost" size="sm" onClick={() => handleDisable(u)}>
                            Disable
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {total > perPage && (
            <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
              <span>Showing {users.length} of {total}</span>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)}>
                  Previous
                </Button>
                <span className="px-3 py-1.5">{page} / {totalPages}</span>
                <Button variant="ghost" size="sm" disabled={page === totalPages} onClick={() => setPage(page + 1)}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <InviteModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInvited={(u) => { setFlash(`Invitation sent to ${u.email}`); load(); }}
        actorRole={actorRole}
      />
    </div>
  );
}
